import { useEffect, useState } from "react";
import { Camera, PenLine, Phone, UserCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AuthImage } from "@/components/AuthImage";
import { CameraCapture } from "@/components/CameraCapture";
import { SignaturePad } from "@/components/SignaturePad";
import { IdCard } from "@/components/IdCard";
import { Field, PageHeader } from "@/components/common";

const PhotoBox = ({ p, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [v, setV] = useState(0);
  const upload = async (blob) => {
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", blob, "foto.jpg"); const { data } = await api.post("/profile/photo", fd); toast.success("Foto profil disimpan"); setOpen(false); setV((x) => x + 1); onSaved(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const empty = <div className="h-28 w-28 rounded-full bg-muted grid place-items-center"><UserCircle2 className="h-14 w-14 text-muted-foreground" /></div>;
  return (
    <div className="flex items-center gap-5" data-testid="profile-photo-section">
      {p.has_photo ? <AuthImage path={`/users/${p.id}/photo?v=${v}`} alt="Foto profil" className="h-28 w-28 rounded-full object-cover border" testId="profile-photo" fallback={empty} /> : empty}
      <div>
        <p className="font-heading text-lg font-bold">{p.name}</p>
        <p className="text-sm text-muted-foreground">{ROLE_LABELS[p.role]} · {p.email}</p>
        <Button size="sm" variant="outline" className="mt-2 rounded-full h-9" onClick={() => setOpen(true)} data-testid="profile-photo-button"><Camera className="h-4 w-4 mr-1" />{p.has_photo ? "Ganti Foto" : "Upload Foto"}</Button>
      </div>
      <CameraCapture open={open} onOpenChange={setOpen} onConfirm={upload} busy={busy} title="Foto Profil" description="Foto dipakai untuk ID Card dan penanda di peta." initialFacing="user" />
    </div>
  );
};

const PhoneBox = ({ p, onSaved }) => {
  const [phone, setPhone] = useState(p.phone || "");
  const save = async () => { try { const { data } = await api.put("/profile", { phone }); toast.success("Nomor WhatsApp disimpan"); onSaved(data); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Field label="Nomor WhatsApp">
      <div className="flex gap-2">
        <div className="relative flex-1"><Phone className="h-4 w-4 absolute left-3 top-3.5 text-muted-foreground" /><Input inputMode="tel" className="h-11 pl-9" placeholder="cth. 081234567890" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d+]/g, ""))} data-testid="profile-phone-input" /></div>
        <Button className="rounded-full h-11 px-5" onClick={save} data-testid="profile-phone-save">Simpan</Button>
      </div>
    </Field>
  );
};

const SignBox = ({ p, onSaved }) => {
  const [redo, setRedo] = useState(!p.has_signature);
  const [busy, setBusy] = useState(false);
  const [v, setV] = useState(0);
  const save = async (blob) => {
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", blob, "ttd.png"); const { data } = await api.post("/profile/signature", fd); toast.success("Tanda tangan disimpan"); setRedo(false); setV((x) => x + 1); onSaved(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="panel space-y-3" data-testid="profile-signature-section">
      <div className="flex items-center gap-2"><PenLine className="h-5 w-5 text-primary" /><h2 className="font-heading text-lg font-bold">Tanda Tangan Digital</h2></div>
      <p className="text-sm text-muted-foreground">Dipakai pada kontrak pinjaman di bagian Petugas / Koperasi.</p>
      {redo ? <SignaturePad busy={busy} onSave={save} /> : (
        <>
          <AuthImage path={`/users/${p.id}/signature?v=${v}`} alt="Tanda tangan" className="w-full h-32 rounded-2xl object-contain bg-white border" testId="profile-signature" />
          <Button variant="outline" className="rounded-full h-10" onClick={() => setRedo(true)} data-testid="profile-signature-redo">Ganti Tanda Tangan</Button>
        </>
      )}
    </section>
  );
};

export default function ProfilePage() {
  const { user } = useAuth();
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/profile").then((r) => setP(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!p) return <p className="text-muted-foreground">Memuat...</p>;
  const card = { ...p, cabang_name: user.cabang_name, wilayah_name: user.wilayah_name };
  return (
    <div className="grid lg:grid-cols-[1fr_300px] gap-8 max-w-5xl">
      <div className="space-y-6 min-w-0">
        <PageHeader title="Profil Akun" />
        <section className="panel space-y-6"><PhotoBox p={p} onSaved={setP} /><PhoneBox p={p} onSaved={setP} /></section>
        {p.role === "petugas" && <SignBox p={p} onSaved={setP} />}
      </div>
      <div><p className="font-heading font-semibold mb-3 text-center">ID Card Saya</p><IdCard u={card} /></div>
    </div>
  );
}
